import React, { useMemo } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import { PieChart, Pie, Cell } from "recharts";
import { useAuditoria } from "../contexts/AuditoriaContext";
import { useAuditoriaUtils } from "../hooks/useAuditoriaUtils";

const COLORS = ["#10b981", "#ef4444"];

const AuditoriaActions = () => {
  const {
    modoAuditoria,
    selectedClinica,
    selectedOdontologo,
    auditingOdontologo,
    auditingPaciente,
    auditingRows,
    filtroPrestaciones,
    updateAuditoria,
  } = useAuditoria();

  const { handleAuditarOdontologo, handleAuditarPaciente } =
    useAuditoriaUtils();

 const handleFiltroChange = (_event: any, newValue: string) => {
  if (newValue) {
    updateAuditoria({ filtroPrestaciones: newValue })
  }
};


  const isDisabled =
    !modoAuditoria ||
    auditingOdontologo ||
    auditingPaciente ||
    !selectedClinica ||
    !selectedOdontologo ||
    auditingRows.length === 0;

  const chartData = useMemo(() => {
    const total = auditingRows.length;
    const rechazadas = auditingRows.filter((row: any) =>
      ["F", "R", "A"].includes(row.resultado_auditoria)
    ).length;
    const aprobadas = total - rechazadas;

    return [
      { name: "Aprobadas", value: aprobadas },
      { name: "Rechazadas", value: rechazadas },
    ];
  }, [auditingRows]);

  const porcentajeRechazo = useMemo(() => {
    const total = auditingRows.length;
    const rechazadas = auditingRows.filter((row: any) =>
      ["F", "R", "A"].includes(row.resultado_auditoria)
    ).length;
    return total > 0 ? Math.round((rechazadas / total) * 100) : 0;
  }, [auditingRows]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "nowrap",
      }}
    >
      {/* Filtro de prestaciones */}
      <ToggleButtonGroup
        size="small"
        value={filtroPrestaciones}
        exclusive
        onChange={handleFiltroChange}
        aria-label="filtro prestaciones"
        sx={{ height: 40 }}
      >
        <ToggleButton value="Todos">Todos</ToggleButton>
        <ToggleButton value="Aprobados">Aprobados</ToggleButton>
        <ToggleButton value="Rechazados">Rechazados</ToggleButton>
      </ToggleButtonGroup>

      {/* Mini gráfico con % rechazo */}
      <Box sx={{ position: "relative", width: 40, height: 40 }}>
        <PieChart width={40} height={40}>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={12}
            outerRadius={20}
            dataKey="value"
            stroke="none"
          >
            {chartData.map((_entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index]} />
            ))}
          </Pie>
        </PieChart>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}
        >
          <Typography variant="caption" fontSize={10}>
            {porcentajeRechazo}%
          </Typography>
        </Box>
      </Box>

      {/* Botones */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          flexDirection: "row",
          flexShrink: 0,
        }}
      >
        <LoadingButton
          size="small"
          loading={auditingOdontologo}
          loadingPosition="start"
          startIcon={<PeopleIcon />}
          variant="contained"
          onClick={handleAuditarOdontologo}
          disabled={isDisabled}
          sx={{
            minWidth: 140,
            height: 40,
            backgroundColor: "primary.main",
            textTransform: "capitalize",
            ":hover": {
              bgcolor: "#475569",
              color: "white",
            },
          }}
        >
          Auditar Todos
        </LoadingButton>

        <LoadingButton
          size="small"
          loading={auditingPaciente}
          loadingPosition="start"
          startIcon={<PersonIcon />}
          variant="contained"
          onClick={handleAuditarPaciente}
          disabled={isDisabled}
          sx={{
            minWidth: 140,
            height: 40,
            backgroundColor: "primary.main",
            textTransform: "capitalize",
            ":hover": {
              bgcolor: "#475569",
              color: "white",
            },
          }}
        >
          Auditar Paciente
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default AuditoriaActions;
